import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLogTime } from '../../../state/data/interaction';
import {
  validateManualEntry, ymd, resolveAuthorId, MIN_MINUTES_LOG_TIME, MAX_MINUTES_LOG_TIME,
} from '../../../logic/timeTracking';
import useDemoMode from '../../../hooks/useDemoMode';

// Manual interaction-time entry (wpc-front "Log time" parity: 1–120 min,
// date bounded to [care-period start, today], optional note).
export default function LogTimeModal({ patient, carePeriod, currentUser, onClose }) {
  const demoMode = useDemoMode();
  const logTimeMut = useLogTime();
  const todayYmd = ymd(new Date());
  const minYmd = (carePeriod?.start_date || '').slice(0, 10) || null;
  const [minutes, setMinutes] = useState('2');
  const [dateYmd, setDateYmd] = useState(todayYmd);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    // Capture + stopImmediatePropagation: Escape closes ONLY this form — not
    // the Time log modal behind it, not the slide-over drawer.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    // Restore the PREVIOUS value — this modal can be nested inside TimeLogModal,
    // whose own scroll lock must survive this one closing.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleSubmit = () => {
    const error = validateManualEntry({ minutes, dateYmd, minYmd, maxYmd: todayYmd });
    setValidationError(error);
    if (error) return;
    logTimeMut.mutate(
      {
        userid: patient.userid,
        minutes: Number(minutes),
        dateYmd,
        authorid: resolveAuthorId(currentUser),
        note: note.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  // Portaled to body — the drawer's transform breaks position:fixed otherwise.
  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="header3">Log time</h2>
          <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="modal-body">
          <p className="body2 text-secondary" style={{ marginTop: 0 }}>
            Add interaction time spent with <span className={demoMode ? 'demo-blur' : undefined}>{patient.firstname || 'this patient'}</span> outside the tracked sessions.
          </p>
          <div className="rx-field">
            <label className="eyebrow" htmlFor="log-time-minutes">Time (minutes)</label>
            <input
              id="log-time-minutes"
              className="rx-input"
              type="number"
              min={MIN_MINUTES_LOG_TIME}
              max={MAX_MINUTES_LOG_TIME}
              step={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
          <div className="rx-field">
            <label className="eyebrow" htmlFor="log-time-date">Date</label>
            <input
              id="log-time-date"
              className="rx-input"
              type="date"
              min={minYmd || undefined}
              max={todayYmd}
              value={dateYmd}
              onChange={(e) => setDateYmd(e.target.value)}
            />
          </div>
          <div className="rx-field">
            <label className="eyebrow" htmlFor="log-time-note">Note (optional)</label>
            <textarea
              id="log-time-note"
              className="rx-input"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {(validationError || logTimeMut.error) && (
            <div className="error-banner">{validationError || String(logTimeMut.error)}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={logTimeMut.isPending}>
            {logTimeMut.isPending ? 'Saving…' : 'Log time'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
